import { security } from '@beelday/common'
import { Config } from 'common/config'
import { RoomAddress } from 'common/types'
import useMicApi from 'features/microphone/microphone-api'
import { useMemo } from 'react'
import { useAssertedJoinedRoomAddress } from 'room/common/use-joined-room-address'

/**
 * What the trainer does with the film or recording on the stage, whichever
 * control sends it - the player's own or, in presenter mode, the console's
 * remote. Offsets are in milliseconds.
 */
export type MediaCommands = {
	play: (offsetMs: number) => Promise<void>
	pause: (offsetMs: number) => Promise<void>
	/** Moves to `offsetMs`, playing on or staying paused. */
	seek: (offsetMs: number, playing: boolean) => Promise<void>
	/** The player reached the end. */
	finish: () => Promise<void>
	close: () => Promise<void>
}

/** The part of the YouTube and audio streaming APIs the commands use. */
export type MediaStreamApi = {
	play: (address: RoomAddress, offset: number) => Promise<void>
	pause: (address: RoomAddress, offset: number) => Promise<void>
	finish: (address: RoomAddress) => Promise<void>
	removeCurrent: (address: RoomAddress) => Promise<void>
}

/**
 * The commands of a media stream in the joined room. The player mutes the
 * trainer's microphone while it plays; every command that stops the playback
 * gives it back.
 */
export const useMediaCommands = (api: MediaStreamApi): MediaCommands => {
	const address = useAssertedJoinedRoomAddress()
	const microphoneApi = useMicApi(Config.beeldayBackendUrl)
	const { id: userId } = security.useAuthenticatedUser()

	return useMemo<MediaCommands>(() => {
		const unmute = (): Promise<void> => microphoneApi.unmuteMic(address, userId)
		return {
			play: offsetMs => api.play(address, offsetMs),
			pause: offsetMs => api.pause(address, offsetMs).then(unmute),
			seek: (offsetMs, playing) =>
				playing ? api.play(address, offsetMs) : api.pause(address, offsetMs),
			finish: () => api.finish(address).then(unmute),
			close: () => api.removeCurrent(address).then(unmute),
		}
	}, [address, api, microphoneApi, userId])
}
